import { Box } from "@mui/material";
import React, {
  useState,
  useEffect,
  type ChangeEvent,
  type ComponentProps,
} from "react";
import { getRoles, getOrganizations } from "../../../api";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import SearchResult from "./SearchResult";
import DatePickerJapanese from "../../../components/DatePickerJapanese";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TablePagination from "@mui/material/TablePagination";
import { parse } from "date-fns";
import type { OrganizationItem } from "../../../types/organization";
import type { RoleItem } from "../../../types/role";
import type { MailSearchContents } from "../../../types/mail";

type CheckboxColumn = "m_role_id" | "m_organization_id";

type CheckboxValidateProps = {
  roleId: number;
  column: CheckboxColumn;
  organizationIds?: number[];
};

export default function MailLog() {
  const [searchContents, setSearchContents] = useState<MailSearchContents>({
    sent_at_stt: null,
    sent_at_end: null,
    is_plan: null,
    title: null,
    m_role_id: null,
    m_organization_id: null,
    order: null,
    sort: null,
    page: null,
    limit: null,
  });

  const CheckboxValidate = ({
    roleId,
    column,
    organizationIds,
  }: CheckboxValidateProps) => {
    const isChecked = searchContents[column]?.includes(roleId) ?? false;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setSearchContents((prevSearchContents) => {
        const currentMRoleId = prevSearchContents[column] || [];
        let updatedMRoleId = currentMRoleId.slice(); // Make a copy of currentMRoleId

        if (organizationIds) {
          updatedMRoleId = e.target.checked
            ? [...updatedMRoleId, ...organizationIds]
            : updatedMRoleId.filter(
                (checkedId) => !organizationIds.includes(checkedId)
              );
        } else {
          updatedMRoleId = e.target.checked
            ? [...updatedMRoleId, roleId]
            : updatedMRoleId.filter((checkedId) => checkedId !== roleId);
        }

        return {
          ...prevSearchContents,
          [column]: updatedMRoleId,
          [column === "m_role_id" ? "m_organization_id" : "m_role_id"]: null,
        };
      });
    };

    return (
      <Checkbox
        size="small"
        checked={isChecked}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e)}
        sx={{ cursor: "pointer", pointerEvents: "auto" }}
      />
    );
  };

  const handleChangeTitle = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    setSearchContents((prevValue) => ({
      ...prevValue,
      title: newValue,
    }));
  };

  const handleChangeCalender = (
    e: Date | null,
    column?: string
  ) => {
    if (column !== "sent_at_stt" && column !== "sent_at_end") {
      return;
    }
    // クリア時（eがnullの場合）はnullを設定
    if (e === null) {
      setSearchContents((prevValue) => ({
        ...prevValue,
        [column]: null,
      }));
      return;
    }

    const year = e.getFullYear();
    const month = (e.getMonth() + 1).toString().padStart(2, "0");
    const day = e.getDate().toString().padStart(2, "0");
    const hours = e.getHours().toString().padStart(2, "0");
    const minutes = e.getMinutes().toString().padStart(2, "0");
    const seconds = "00"; // SSは常に"00"に設定

    const formattedDateStr = `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    console.log("eventlog", formattedDateStr);

    setSearchContents((prevValue) => ({
      ...prevValue,
      [column]: formattedDateStr,
    }));
  };

  const [roles, setRoles] = useState<RoleItem[] | null>(null);
  const fetchRoles = async () => {
    try {
      const response = await getRoles({ order: null, sort: null, page: 1 });
      if (response.status !== "ok") {
        return;
      }
      const data = response.m_roles;
      console.log("権限情報情報取得", response);
      setRoles(data);
      console.log("data", data);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  const [organizations, setOrganizations] = useState<OrganizationItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalData, setTotalData] = useState<number | undefined>();

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const fetchOrganizations = async () => {
    try {
      const response = await getOrganizations({
        parent_m_organization_id: 0,
        m_branch_id: null,
        m_role_id: null,
        order: null,
        sort: null,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        return;
      }
      console.log("組織情報取得", response);
      const data = response.m_organizations;
      const total = response.total_count;
      setOrganizations(data);
      setTotalData(total);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const flattenOrganizationIds = (org: OrganizationItem): number[] => {
    let ids: number[] = [];
    if (org.id) {
      ids.push(org.id);
    }
    if (Array.isArray(org.child_m_organizations)) {
      org.child_m_organizations.forEach((childOrg) => {
        ids = ids.concat(flattenOrganizationIds(childOrg));
      });
    }
    return ids;
  };

  const renderTree = (organization: OrganizationItem) => {
    const organizationIds = flattenOrganizationIds(organization);

    if (!organization) {
      return null;
    }

    return (
      <Box
        key={organization.id}
        sx={{ display: "flex", alignItems: "flex-start", padding: "0" }}
      >
        <TreeItem
          nodeId={String(organization.id)}
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* 親組織のIDをプロップスで渡す */}
              <CheckboxValidate
                roleId={organization.id}
                column="m_organization_id"
                organizationIds={organizationIds}
              />
              {organization.group_name === null
                ? organization.department_name
                : organization.department_name + "-" + organization.group_name}
            </Box>
          }
          sx={{
            display: "flex",
            flexDirection: "column",
            ".MuiTreeItem-iconContainer": {
              display: "none",
            },
            ".css-1vbkhv8-MuiTreeItem-content": {
              padding: "0",
            },
            ".css-1vbkhv8-MuiTreeItem-content .MuiTreeItem-label": {
              padding: "0",
            },
          }}
        >
          {Array.isArray(organization.child_m_organizations)
            ? organization.child_m_organizations.map((childOrganization) =>
                renderTree(childOrganization)
              )
            : null}
        </TreeItem>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: "48px",
          alignItems: "center",
          paddingLeft: "40px",
          fontSize: 20,
          fontWeight: "bold",
          whiteSpace: "nowrap",
          "@media screen and (max-width:900px)": {
            paddingLeft: 2,
          },
        }}
      >
        メール送信履歴
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          borderRadius: 2,
          borderColor: "primary.main",
          boxShadow: 3,
          color: "outline.dark",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            gap: "16px",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              paddingLeft: "40px",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索条件
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "48px",
              "@media screen and (max-width:600px)": {
                flexDirection: "column",
                width: "100%",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
                maxWidth: "480px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  minWidth: "min-content",
                  fontSize: 16,
                  fontWeight: "bold",
                  color: "#000",
                  whiteSpace: "nowrap",
                  "@media screen and (max-width:900px)": {
                    flexDirection: "column",
                    width: "100%",
                    alignItems: "flex-start",
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: "160px",
                    padding: "8px 0 0 0",
                    "@media screen and (max-width:900px)": {
                      padding: "0",
                    },
                  }}
                >
                  権限
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    width: "100%",
                  }}
                >
                  <Box>
                    {roles?.map((role) => (
                      <Box
                        key={role.name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontWeight: "500",
                        }}
                      >
                        <CheckboxValidate roleId={role.id} column="m_role_id" />
                        {role.name}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  minWidth: "min-content",
                  fontSize: 16,
                  fontWeight: "bold",
                  color: "#000",
                  whiteSpace: "nowrap",
                  "@media screen and (max-width:900px)": {
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    width: "100%",
                  },
                }}
              >
                <Box sx={{ minWidth: "160px" }}>件名</Box>
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  size="small"
                  onChange={(e) => handleChangeTitle(e)}
                  sx={{
                    height: "40px",
                    width: "100%",
                    ".MuiInputBase-root": {
                      height: "100%",
                    },
                    "#outlined-basic": {
                      boxSizing: "border-box",
                      height: "100%",
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  minWidth: "min-content",
                  fontSize: 16,
                  fontWeight: "bold",
                  color: "#000",
                  whiteSpace: "nowrap",
                  "@media screen and (max-width:900px)": {
                    flexDirection: "column",
                    width: "100%",
                    alignItems: "flex-start",
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: "160px",
                    padding: "8px 0 0 0",
                    ".MuiFormControl-root": {
                      width: "100% !importnat",
                    },
                  }}
                >
                  送信日時
                </Box>
                {/* <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: "8px",
                    "@media screen and (max-width:900px)": {
                      width: "100%",
                      flexDirection: "column",
                    },
                  }}
                >
                  <DatePickerJapanese
                    label="start"
                    type="datetime"
                    inputFormat="yyyy-MM-dd"
                    format={undefined}
                    placeholder="****年**月**日"
                    onChange={handleChangeCalender}
                    column="sent_at_stt"
                  />
                  <DatePickerJapanese
                    label="end"
                    type="datetime"
                    inputFormat="yyyy-MM-dd"
                    placeholder="****年**月**日"
                    onChange={handleChangeCalender}
                    column="sent_at_end"
                  />
                </Box> */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: "8px",
                    "@media screen and (max-width:900px)": {
                      width: "100%",
                      flexDirection: "column",
                    },
                  }}
                >
                  <DatePickerJapanese
                    label="start"
                    type="datetime"
                    inputFormat="yyyy-MM-dd"
                    format={undefined}
                    placeholder="****年**月**日"
                    value={
                      searchContents.sent_at_stt
                        ? parse(
                            searchContents.sent_at_stt,
                            "yyyy/MM/dd HH:mm:ss",
                            new Date()
                          )
                        : null
                    }
                    onChange={handleChangeCalender}
                    column="sent_at_stt"
                  />
                  <DatePickerJapanese
                    label="end"
                    type="datetime"
                    inputFormat="yyyy-MM-dd"
                    format="yyyy-MM-dd HH:mm"
                    placeholder="****年**月**日"
                    value={
                      searchContents.sent_at_end
                        ? parse(
                            searchContents.sent_at_end,
                            "yyyy/MM/dd HH:mm:ss",
                            new Date()
                          )
                        : null
                    }
                    onChange={handleChangeCalender}
                    column="sent_at_end"
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
                maxWidth: "480px",
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    minWidth: "min-content",
                    fontSize: 16,
                    fontWeight: "bold",
                    color: "#000",
                    whiteSpace: "nowrap",
                    "@media screen and (max-width:900px)": {
                      flexDirection: "column",
                      width: "100%",
                    },
                  }}
                >
                  <Box
                    sx={{
                      minWidth: "160px",
                      padding: "8px 0 0 0",
                      "@media screen and (max-width:900px)": {
                        padding: "0",
                      },
                    }}
                  >
                    会社・部署・Gr
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                        fontWeight: "500",
                      }}
                    >
                      <TreeView
                        defaultCollapseIcon={<ExpandMoreIcon />}
                        defaultExpandIcon={<ChevronRightIcon />}
                        multiSelect
                      >
                        {Array.isArray(organizations)
                          ? organizations?.map((organization) =>
                              renderTree(organization)
                            )
                          : null}
                      </TreeView>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    minWidth: "min-content",
                    fontSize: 16,
                    fontWeight: "bold",
                    color: "#000",
                    whiteSpace: "nowrap",
                    "@media screen and (max-width:900px)": {
                      flexDirection: "column",
                      width: "100%",
                    },
                  }}
                >
                  <TablePagination
                    rowsPerPageOptions={[10, 30, 50, 100]}
                    component="div"
                    count={totalData ?? 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
                        {
                          margin: "0 !important",
                          padding: "0 !important",
                        },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <SearchResult searchContents={searchContents} />
      </Box>
    </>
  );
}
